import React from 'react';
import { Dayjs } from 'dayjs';

import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';

interface UploadHistoryFilterProps {
  beforeDate: Dayjs | null;
  onBeforeDateChange: (value: Dayjs | null) => void;
  onStatusFilterChange: (value: string) => void;
  onArchivedSubFilterChange: (value: string) => void;
}

function UploadHistoryFilter({ beforeDate, onBeforeDateChange, onStatusFilterChange, onArchivedSubFilterChange }: UploadHistoryFilterProps) {
  const handleStatusFilterChange = (value: string) => {
    const isArchived = value === 'archived';
    onStatusFilterChange(value);
    
    // Update the archived sub-menu visibility via DOM manipulation (no re-render!)
    const subMenu = document.getElementById('archived-sub-menu');
    const topMenu = document.getElementById('status-main-menu');
    
    if (subMenu && topMenu) {
      if (isArchived) {
        // Simple fade in for sub-menu - use visibility to maintain space
        subMenu.style.display = 'block';
        subMenu.style.visibility = 'visible';
        subMenu.style.opacity = '1';
        subMenu.style.animation = 'fadeIn 0.4s ease-in-out';
      } else {
        // Fade out sub-menu - keep space reserved
        subMenu.style.animation = 'fadeOut 0.4s ease-in-out';
        setTimeout(() => {
          subMenu.style.visibility = 'hidden';
          subMenu.style.opacity = '0';
          // Keep display: block to maintain space, just hide visually
        }, 400); // Match animation duration
      }
    }
  };

  return (
    <Card className="mb-4">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div className="w-full md:w-auto">
            <DateTimePicker
              label="Before Date"
              value={beforeDate}
              onChange={onBeforeDateChange}
              className="w-full md:w-[250px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex flex-col gap-3 upload-history-status-container">
              <RadioGroup
                id="status-main-menu"
                defaultValue="archived"
                onValueChange={handleStatusFilterChange}
                className="flex flex-row gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="upcoming" id="upcoming" />
                  <Label htmlFor="upcoming" className="cursor-pointer">Upcoming</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="archived" id="archived" />
                  <Label htmlFor="archived" className="cursor-pointer">Archived</Label>
                </div>
              </RadioGroup>
              
              <div 
                id="archived-sub-menu"
                className="ml-6 pl-4 border-l-2 border-border"
                style={{
                  display: 'block',
                  visibility: 'visible',
                  opacity: '1'
                }}
              >
                <RadioGroup
                  defaultValue="archived"
                  onValueChange={onArchivedSubFilterChange}
                  className="flex flex-row gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="archived" id="archived-all" />
                    <Label htmlFor="archived-all" className="cursor-pointer">All</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="archived-scheduled" id="archived-scheduled" />
                    <Label htmlFor="archived-scheduled" className="cursor-pointer">Scheduled</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="archived-instant" id="archived-instant" />
                    <Label htmlFor="archived-instant" className="cursor-pointer">Instant</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Memoization: only re-render if beforeDate actually changes
export default React.memo(UploadHistoryFilter, (prevProps, nextProps) => {
  // Only re-render if beforeDate changes (handlers are always stable)
  return prevProps.beforeDate?.valueOf() === nextProps.beforeDate?.valueOf();
});

